import {
	HttpEvent,
	HttpHandler,
	HttpInterceptor,
	HttpRequest,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MsalService } from "@azure/msal-angular";
import {
	AccountInfo,
	AuthenticationResult,
	InteractionRequiredAuthError,
} from "@azure/msal-browser";
import { EMPTY, Observable, from, of } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { UserProfileStore } from "../stores/user-profile.store";

/** Margen para considerar un ID token "a punto de caducar" y renovarlo. */
const TOKEN_RENEWAL_OFFSET_MS = 60 * 1000;

/**
 * Adjunta a las llamadas a la API propia un ID token SIEMPRE vigente,
 * obtenido de MSAL con renovación silenciosa (acquireTokenSilent).
 *
 * Historia: la versión anterior leía el ID token cacheado directamente de
 * localStorage, sin renovación posible → al caducar (~1h) toda la API
 * devolvía 401 y la app quedaba clavada en /error hasta borrar caché.
 *
 * Ámbito: SOLO environment.api.root. Graph lo gestiona MsalInterceptor
 * (protectedResourceMap) y a terceros (Nominatim, assets) no se les debe
 * enviar el token corporativo.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
	// Mismos scopes que MSALGuardConfigFactory: renovar con lo que se hizo login.
	private readonly scopes = ["user.read", "contacts.read"];

	constructor(
		private readonly msalService: MsalService,
		private readonly _userProfileStore: UserProfileStore
	) {}

	intercept(
		request: HttpRequest<any>,
		next: HttpHandler
	): Observable<HttpEvent<any>> {
		if (!request.url.startsWith(environment.api.root)) {
			return next.handle(request);
		}

		// initialize() es idempotente: garantiza que MSAL está listo aunque la
		// llamada llegue antes de que el guard procese la redirección de login.
		return from(this.msalService.instance.initialize()).pipe(
			switchMap(() => {
				const account =
					this.msalService.instance.getActiveAccount() ??
					this.msalService.instance.getAllAccounts()[0] ??
					null;

				if (!account) {
					// Sin cuenta no hay token que renovar: el MsalGuard de la ruta
					// forzará el login; la llamada saldrá anónima y el 401 la
					// gestionará ErrorInterceptor.
					return next.handle(request);
				}

				return this.acquireFreshIdToken(account).pipe(
					switchMap((result) =>
						next.handle(this.withAuthHeaders(request, result.idToken))
					)
				);
			}),
			catchError((error) => {
				if (error instanceof InteractionRequiredAuthError) {
					// La sesión ya no se puede renovar en silencio (SSO caducado,
					// consentimiento, CA policy…): re-login por redirect.
					this.msalService
						.acquireTokenRedirect({ scopes: this.scopes })
						.subscribe();
					return EMPTY;
				}
				// Fallo transitorio de MSAL (red, iframe timeout…): dejar salir la
				// petición sin token; el error resultante sigue el flujo normal.
				return next.handle(request);
			})
		);
	}

	private acquireFreshIdToken(
		account: AccountInfo
	): Observable<AuthenticationResult> {
		return this.msalService
			.acquireTokenSilent({ scopes: this.scopes, account })
			.pipe(
				switchMap((result) => {
					// MSAL decide renovar por la caducidad del ACCESS token: puede
					// devolver un ID token cacheado ya vencido. Si es el caso, forzar
					// la renovación (usa el refresh token, sin interacción).
					if (this.isIdTokenExpired(result)) {
						return this.msalService.acquireTokenSilent({
							scopes: this.scopes,
							account,
							forceRefresh: true,
						});
					}
					return of(result);
				})
			);
	}

	private isIdTokenExpired(result: AuthenticationResult): boolean {
		const exp = (result.idTokenClaims as { exp?: number })?.exp;
		return !!exp && exp * 1000 <= Date.now() + TOKEN_RENEWAL_OFFSET_MS;
	}

	private withAuthHeaders(
		request: HttpRequest<any>,
		idToken: string
	): HttpRequest<any> {
		let headers = request.headers
			.set("Authorization", `Bearer ${idToken}`)
			.set(
				"UtcOffset",
				(new Date().getTimezoneOffset() / 60).toString()
			);

		const userProfile = this._userProfileStore.getUserProfile();
		if (userProfile) {
			headers = headers.set("UserProfile", JSON.stringify(userProfile));
		}

		return request.clone({ headers });
	}
}
